import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { boardsAPI, Board } from '../api/api';
import { useAuth } from '../context/AuthContext';
import Navbar from './Navbar';
import CreateBoardModal from './CreateBoardModal';
import './BoardList.css';

const BoardList: React.FC = () => {
  const { isAdmin, isManager } = useAuth();
  const canManageBoards = isAdmin || isManager;
  const [boards, setBoards] = useState<Board[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  const { user, isImpersonating } = useAuth();

  useEffect(() => {
    loadBoards();
  }, [user?.id, isImpersonating]);

  const loadBoards = async () => {
    try {
      setLoading(true);
      const data = await boardsAPI.getAll();
      setBoards(data);
    } catch (error) {
      console.error('Ошибка при загрузке досок:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBoardCreated = () => {
    loadBoards();
    setShowCreateModal(false);
  };

  const handleDeleteBoard = async (boardId: number) => {
    if (!window.confirm('Вы уверены, что хотите удалить эту доску?')) {
      return;
    }

    try {
      await boardsAPI.delete(boardId);
      loadBoards();
    } catch (error) {
      console.error('Ошибка при удалении доски:', error);
    }
  };

  const filteredBoards = boards.filter(board => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return board.name.toLowerCase().includes(query);
  });

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="loading">Загрузка...</div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="board-list-container">
        <div className="board-list-header">
          <h1>Доски проектов</h1>
          <div className="header-actions">
            <input
              type="text"
              placeholder="Поиск по доскам..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            {canManageBoards && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="create-board-btn"
              >
                + Создать доску
              </button>
            )}
          </div>
        </div>

        <div className="boards-grid">
          {filteredBoards.length === 0 ? (
            <div className="empty-state">
              <p>
                {searchQuery.trim() 
                  ? `Не найдено досок по запросу "${searchQuery}"` 
                  : 'Нет досок. Создайте первую доску для начала работы!'}
              </p>
            </div>
          ) : (
            filteredBoards.map((board) => (
              <div
                key={board.id}
                className="board-card"
                onClick={() => navigate(`/board/${board.id}`)}
              >
                <div className="board-card-header">
                  <h3>{board.name}</h3>
                  {canManageBoards && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteBoard(board.id);
                      }}
                      className="close-btn"
                      title="Удалить доску"
                    >
                      ×
                    </button>
                  )}
                </div>
                {board.description && (
                  <p className="board-description">{board.description}</p>
                )}
                <div style={{ marginTop: '8px', marginBottom: '8px' }}>
                  <span style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    backgroundColor: board.visibility === 'private' ? '#ff9800' : '#4caf50',
                    color: 'white',
                    fontWeight: '500'
                  }}>
                    {board.visibility === 'private' ? '🔒 Приватный' : '🌐 Общий'}
                  </span>
                  {board.visibility === 'private' && (
                    <span style={{ marginLeft: '8px', fontSize: '12px', color: '#666' }}>
                      Участников: {(board.members?.length || 0) + 1}
                    </span>
                  )}
                </div>
                <div className="board-card-footer">
                  <span className="board-meta">
                    Создал: {board.created_by_username}
                  </span>
                  <span className="board-task-count">
                    Задач: {board.task_count || 0}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>

        {showCreateModal && (
          <CreateBoardModal
            onClose={() => setShowCreateModal(false)}
            onBoardCreated={handleBoardCreated}
          />
        )}
      </div>
    </>
  );
};

export default BoardList;


